import { useCallback, useState } from 'react';

export function useTableSelection<T>({ items, getItemId }: SelectionProps<T>) {
  const extractId = useCallback(
    (item: T) => getItemId(item).toString(),
    [getItemId],
  );
  const [selection, setSelection] = useState<Set<string>>(new Set());

  const onSelect = useCallback(
    (item: T) => {
      const id = extractId(item);
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [extractId],
  );

  const onSelectAll = useCallback(() => {
    setSelection((prev) => {
      const allIds = items.map((item) => extractId(item));
      const allSelected = allIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(allIds);
    });
  }, [items, extractId]);

  const isSelectedAll =
    items.length > 0 && items.every((item) => selection.has(extractId(item)));

  const clearSelection = useCallback(() => {
    setSelection(new Set());
  }, []);

  return {
    isSelectedAll,
    onSelect,
    onSelectAll,
    selection,
    clearSelection,
  };
}

interface SelectionProps<T> {
  items: T[];
  getItemId: (item: T) => string | number;
}
